/**
 * useOnboardingState — Decides what the dashboard shows on login.
 *
 * Logic (Epic 14 Phase 9, section 7d):
 *   User logs in → useSignupIntent reads Tenant.signup_intent + onboarding_completed
 *     Has intent + onboarding_completed=false? → "intent-onboarding"
 *     Has intent + onboarding_completed=true? → "active"
 *     No intent? → "generic" (current 3-path picker, zero regression)
 *
 * Onboarding state lives on the Tenant record (server-side). Admin can
 * toggle it on/off from control.zaviago.com → Tenant form.
 */
import { useSignupIntent } from "./useSignupIntent";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { useQueryClient } from "@tanstack/react-query";
import { cpCompleteOnboarding } from "@/lib/api/controlPlaneSignupContext";

/**
 * Maps signup intents to dashboard onboarding path IDs.
 * Duplicated from useSignupIntent for the dev override path.
 */
const INTENT_TO_PATH: Record<string, string> = {
  chat: "chat",
  selling: "selling",
  pos: "selling",
  crm: "contacts",
};

export type OnboardingView = "intent-onboarding" | "generic" | "active";

export interface OnboardingState {
  /** Which view the dashboard should render */
  view: OnboardingView;
  /** The signup intent (chat/selling/pos/crm) or null */
  intent: string | null;
  /** The dashboard path ID this intent maps to */
  pathId: string | null;
  /** Qualification answers from registration (channels, category, etc.) */
  qualification: Record<string, unknown> | null;
  /** Whether the signup context is still loading */
  isLoading: boolean;
  /** Mark onboarding as complete — hides intent-specific page permanently */
  completeOnboarding: () => void;
  /** Skip/dismiss onboarding — same as complete but user chose to skip */
  skipOnboarding: () => void;
  /** Reset onboarding state (for dev/testing) */
  resetOnboarding: () => void;
}

export function useOnboardingState(): OnboardingState {
  const { data: signupContext, isLoading } = useSignupIntent();
  const intentOverride = usePreferencesStore((s) => s.signupIntentOverride);
  const queryClient = useQueryClient();

  // Dev override: when set, use the override intent instead of real data
  const useOverride = intentOverride !== "auto";
  const intent = useOverride ? intentOverride : (signupContext?.intent ?? null);
  const pathId = useOverride ? (INTENT_TO_PATH[intentOverride] ?? null) : (signupContext?.pathId ?? null);
  const qualification = useOverride ? null : (signupContext?.qualification ?? null);

  // Server-side onboarding state — admin can toggle from control.zaviago.com
  const isOnboardingComplete = useOverride ? false : (signupContext?.onboardingCompleted ?? false);

  const completeOnboarding = () => {
    // Fire-and-forget — sets onboarding_completed=1 on the Tenant record
    cpCompleteOnboarding().catch(() => {});
    // Optimistically update the cached query so UI reflects immediately
    queryClient.setQueryData(["signup-context"], (old: Record<string, unknown> | undefined) =>
      old ? { ...old, onboarding_completed: true } : old
    );
  };

  // Determine view
  // localStorage fallback: if the chat→inbox redirect already fired but the
  // server-side cpCompleteOnboarding() failed silently, treat as completed so
  // DashboardPage renders the active view instead of re-triggering the redirect.
  const chatRedirectDone = intent === "chat"
    && localStorage.getItem("chat_onboarding_redirect_done") === "1";

  let view: OnboardingView;
  if (!intent) {
    view = "generic";
  } else if (isOnboardingComplete || chatRedirectDone) {
    view = "active";
  } else {
    view = "intent-onboarding";
  }

  return {
    view,
    intent,
    pathId,
    qualification,
    isLoading,
    completeOnboarding,
    skipOnboarding: completeOnboarding, // Same effect, different semantic
    resetOnboarding: () => {
      // Invalidate the cached query so next render re-fetches from server
      queryClient.invalidateQueries({ queryKey: ["signup-context"] });
    },
  };
}
